import React, { useEffect, useState } from "react";
import { http, requestErrorMessage } from "@/lib/erp";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Barcode } from "@/components/Barcode";
import PackingSlipPrint from "@/components/PackingSlipPrint";
import { Plus, Search, Truck, Trash2, Printer } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  invoice_id: "", bill_no: "",
  to_name: "", to_phone: "", to_address: "", to_city: "", to_state: "", to_pincode: "",
  from_name: "", from_address: "", from_city: "", from_state: "", from_pincode: "",
  transporter: "", vehicle_no: "", packages: 1, weight: "", notes: "",
};

export default function Transport() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [company, setCompany] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [slip, setSlip] = useState(null);
  const [slipMode, setSlipMode] = useState("a4");
  const [loadError, setLoadError] = useState("");
  const [referenceError, setReferenceError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const { data } = await http.get("/transports", { params: { search } });
      setRows(data);
    } catch (error) {
      setLoadError(requestErrorMessage(error, "load transports"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search]);
  const loadReferences = async () => {
    setReferenceError("");
    const [companyResult, invoicesResult] = await Promise.allSettled([http.get("/company"), http.get("/invoices")]);
    const errors = [];
    if (companyResult.status === "fulfilled") setCompany(companyResult.value.data);
    else errors.push(requestErrorMessage(companyResult.reason, "load company details"));
    if (invoicesResult.status === "fulfilled" && Array.isArray(invoicesResult.value.data)) setInvoices(invoicesResult.value.data.filter((i) => i.status !== "cancelled"));
    else if (invoicesResult.status === "fulfilled") errors.push("Could not load invoices because the response was not in the expected format.");
    else errors.push(requestErrorMessage(invoicesResult.reason, "load invoices"));
    if (errors.length) setReferenceError(errors.join(" "));
  };
  useEffect(() => { loadReferences(); }, []);

  const openNew = () => {
    setForm({ ...EMPTY,
      from_name: company?.name || "", from_address: company?.address || "",
      from_city: company?.city || "", from_state: company?.state || "", from_pincode: company?.pincode || "" });
    setOpen(true);
  };
  const txt = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // Picking a bill auto-reflects Consignor (company) and Consignee (invoice customer).
  const pickInvoice = async (id) => {
    const inv = invoices.find((i) => i.id === id);
    if (!inv) return;
    try {
      const { data } = await http.get(`/invoices/${id}`);
      const toAddr = data.shipping_address || data.billing_address || "";
      setForm((f) => ({
        ...f, invoice_id: id, bill_no: data.invoice_no,
        to_name: data.customer_name, to_phone: data.customer_phone || "",
        to_address: toAddr, to_city: "", to_state: "", to_pincode: "",
        from_name: company?.name || f.from_name, from_address: company?.address || f.from_address,
        from_city: company?.city || f.from_city, from_state: company?.state || f.from_state, from_pincode: company?.pincode || f.from_pincode,
      }));
    } catch (e) { toast.error(requestErrorMessage(e, "load the invoice address")); }
  };

  const save = async () => {
    if (!form.bill_no.trim()) return toast.error("Select a bill / invoice");
    if (!form.to_name.trim()) return toast.error("Enter the Consignee name");
    if (!form.to_address.trim()) return toast.error("Enter the To address");
    try {
      const { data } = await http.post("/transports", form);
      toast.success(`Transport ${data.transport_no} created`);
      setOpen(false); setSlip(data); load();
    } catch (e) { toast.error(requestErrorMessage(e, "create this transport")); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete transport?")) return;
    try {
      await http.delete(`/transports/${id}`);
      toast.success("Deleted");
      load();
    } catch (error) { toast.error(requestErrorMessage(error, "delete this transport")); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Transport</h1>
          <p className="text-sm text-muted-foreground">Consignments for <span className="font-medium text-foreground">{company?.name || "your company"}</span> · delivery-address QR packing slips</p>
        </div>
        <Button onClick={openNew} data-testid="add-transport-button"><Plus className="h-4 w-4 mr-1" /> New Transport</Button>
      </div>
      {referenceError && <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{referenceError} <Button variant="outline" size="sm" className="ml-2" onClick={loadReferences}>Retry</Button></div>}
      {loadError && <div role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{loadError} <Button variant="outline" size="sm" className="ml-2" onClick={load}>Retry</Button></div>}

      <Card className="p-0 overflow-hidden">
        <div className="p-4 border-b">
          <div className="relative max-w-sm">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder="Search transport / consignee…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="transport-search" />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 py-3 font-medium">Transport No</th><th className="px-4 py-3 font-medium">Bill No</th><th className="px-4 py-3 font-medium">Consignee</th>
              <th className="px-4 py-3 font-medium">Destination</th>
              <th className="px-4 py-3 font-medium text-right">Pkgs</th><th className="px-4 py-3 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading transports…</td></tr>}
              {rows.map((t) => (
                <tr key={t.id} className="border-t hover:bg-slate-50" data-testid={`transport-row-${t.id}`}>
                  <td className="px-4 py-3 font-mono font-medium">{t.transport_no}</td>
                  <td className="px-4 py-3 font-mono text-xs">{t.bill_no || "—"}</td>
                  <td className="px-4 py-3">{t.to_name}<div className="text-xs text-muted-foreground">{t.to_phone}</div></td>
                  <td className="px-4 py-3 text-xs">{[t.to_address, t.to_city, t.to_state, t.to_pincode].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono">{t.packages || 1}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="outline" onClick={() => setSlip(t)} data-testid={`slip-${t.id}`}><Printer className="h-3.5 w-3.5 mr-1" /> Slip</Button>
                      <button onClick={() => del(t.id)} className="p-1.5 hover:bg-rose-50 rounded" data-testid={`delete-transport-${t.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && !loadError && rows.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground"><Truck className="h-8 w-8 mx-auto mb-2 opacity-40" />No transports yet</td></tr>}            </tbody>
          </table>
        </div>
      </Card>

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Transport</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div>
              <Label>Select Bill / Sales Invoice *</Label>
              <Select value={form.invoice_id} onValueChange={pickInvoice}>
                <SelectTrigger className="mt-1.5" data-testid="transport-bill-select"><SelectValue placeholder="Choose an invoice to auto-fill addresses" /></SelectTrigger>
                <SelectContent>
                  {invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_no} · {i.customer_name}</SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-muted-foreground mt-1">Consignor & Consignee auto-fill from the selected bill and your company.</div>
            </div>

            <div className="grid sm:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="font-heading font-semibold text-sm text-brand-700">From (Consignor)</div>
                <div><Label>Name</Label><Input value={form.from_name} onChange={txt("from_name")} className="mt-1.5" data-testid="transport-from-name" /></div>
                <div><Label>Address</Label><Input value={form.from_address} onChange={txt("from_address")} className="mt-1.5" data-testid="transport-from-address" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>City</Label><Input value={form.from_city} onChange={txt("from_city")} className="mt-1.5" /></div>
                  <div><Label>State</Label><Input value={form.from_state} onChange={txt("from_state")} className="mt-1.5" /></div>
                </div>
                <div><Label>Pincode</Label><Input value={form.from_pincode} onChange={txt("from_pincode")} className="mt-1.5" /></div>
              </div>
              <div className="space-y-3">
                <div className="font-heading font-semibold text-sm text-brand-700">To (Consignee)</div>
                <div><Label>Name *</Label><Input value={form.to_name} onChange={txt("to_name")} className="mt-1.5" data-testid="transport-to-name" /></div>
                <div><Label>Phone</Label><Input value={form.to_phone} onChange={txt("to_phone")} className="mt-1.5" /></div>
                <div><Label>Address *</Label><Input value={form.to_address} onChange={txt("to_address")} className="mt-1.5" data-testid="transport-to-address" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label>City</Label><Input value={form.to_city} onChange={txt("to_city")} className="mt-1.5" /></div>
                  <div><Label>State</Label><Input value={form.to_state} onChange={txt("to_state")} className="mt-1.5" /></div>
                </div>
                <div><Label>Pincode</Label><Input value={form.to_pincode} onChange={txt("to_pincode")} className="mt-1.5" /></div>
              </div>
            </div>

            <div className="grid sm:grid-cols-4 gap-3">
              <div><Label>Transporter</Label><Input value={form.transporter} onChange={txt("transporter")} className="mt-1.5" /></div>
              <div><Label>Vehicle No</Label><Input value={form.vehicle_no} onChange={txt("vehicle_no")} className="mt-1.5 font-mono" /></div>
              <div><Label>Packages</Label><Input type="number" value={form.packages} onChange={(e) => setForm({ ...form, packages: +e.target.value })} className="mt-1.5" /></div>
              <div><Label>Weight (kg)</Label><Input value={form.weight} onChange={txt("weight")} className="mt-1.5" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-transport-button">Create & Generate Slip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Packaging slip dialog — pick print model (Normal A4 two-up / Thermal) then print */}
      <Dialog open={!!slip} onOpenChange={(v) => !v && setSlip(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Packaging Slip</DialogTitle></DialogHeader>
          {slip && (
            <>
              <div className="flex items-center gap-3 no-print flex-wrap">
                <Label className="text-sm">Print model</Label>
                <Select value={slipMode} onValueChange={setSlipMode}>
                  <SelectTrigger className="w-56" data-testid="slip-print-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a4">Normal · A4 (two slips per page)</SelectItem>
                    <SelectItem value="thermal">Thermal (72mm)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="border rounded-lg bg-slate-50 p-3">
                <PackingSlipPrint slip={slip} company={company} mode={slipMode} />
              </div>
              <DialogFooter className="no-print">
                <Button variant="outline" onClick={() => setSlip(null)}>Close</Button>
                <Button variant="print" onClick={() => window.print()} data-testid="print-slip-button"><Printer className="h-4 w-4 mr-1" /> Print Slip</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
